import merchantService from "./merchantService";
import Event, { EventAction } from "common/event";
import { ActionWithDispatch } from "common/actionWithDispatch";
import { Dispatch } from "redux";
import Merchant from "domain/merchant";
import { Fee } from "domain/fee";
import { CustomField } from "domain/customField";
import PaymentOptionDetails from "domain/paymentOptionDetails";
import { ErpIntegrationThin } from "domain/erpIntegration";
import { Tag } from "domain/tag";
import FormFieldSetting, { FormField } from "domain/formFieldSetting";
import { trackApiCallNew } from "features/saveIndicator/trackApiCall";
import ContractExtendedType from "domain/contractExtendedType";

export class MerchantUpdated extends Event {
    constructor(public changes: Partial<Merchant>) {
        super(MerchantUpdated.EventName);
    }

    public static EventName: string = "merchants/updated";
}

export class MerchantsLoaded extends Event {
    constructor(public merchants: Merchant[]) {
        super(MerchantsLoaded.EventName);
    }

    public static EventName: string = "merchants/all-loaded";
}

export class MerchantFeesLoaded extends Event {
    constructor(public fees: Fee[]) {
        super(MerchantFeesLoaded.EventName);
    }

    public static EventName: string = "MERCHANT_FEES_LOADED";
}

export class MerchantFeesAdded extends Event {
    constructor(public fee: Fee) {
        super(MerchantFeesAdded.EventName);
    }

    public static EventName: string = "MERCHANT_FEES_ADDED";
}

export class MerchantFeesUpdated extends Event {
    constructor(public fee: Fee) {
        super(MerchantFeesUpdated.EventName);
    }

    public static EventName: string = "MERCHANT_FEES_UPDATED";
}

export class MerchantFeesDeleted extends Event {
    constructor(public fee: Fee) {
        super(MerchantFeesDeleted.EventName);
    }

    public static EventName: string = "MERCHANT_FEES_DELETED";
}

export class MerchantContractExtendedTypesLoaded extends Event {
    constructor(public contractExtendedTypes: ContractExtendedType[]) {
        super(MerchantContractExtendedTypesLoaded.EventName);
    }

    public static EventName: string = "MERCHANT_CONTRACT_EXTENDED_TYPES_LOADED";
}

export class MerchantCustomFieldsLoaded extends Event {
    constructor(public customFields: CustomField[]) {
        super(MerchantCustomFieldsLoaded.EventName);
    }

    public static EventName: string = "MERCHANT_CUSTOM_FIELDS_LOADED";
}

export class MerchantTagsLoaded extends Event {
    constructor(public tags: Tag[]) {
        super(MerchantTagsLoaded.EventName);
    }

    public static EventName: string = "MERCHANT_TAGS_LOADED";
}

export class MerchantPaymentOptionsLoaded extends Event {
    constructor(public paymentOptionDetails: PaymentOptionDetails[]) {
        super(MerchantPaymentOptionsLoaded.EventName);
    }

    public static EventName: string = "MERCHANT_PAYMENT_OPTIONS_LOADED";
}

export class MerchantFormFieldSettingsLoaded extends Event {
    constructor(public formFieldSettings: FormFieldSetting[]) {
        super(MerchantFormFieldSettingsLoaded.EventName);
    }

    public static EventName: string = "MERCHANT_FORM_FIELD_SETTINGS_LOADED";
}

export class MerchantEditableContractFieldsLoaded extends Event {
    constructor(public editableContractFields: FormField[]) {
        super(MerchantEditableContractFieldsLoaded.EventName);
    }

    public static EventName: string = "MERCHANT_EDITABLE_CONTRACT_FIELDS_LOADED";
}

export class MerchantFormFieldSettingsUpdated extends Event {
    constructor(public formFieldSetting: FormFieldSetting) {
        super(MerchantFormFieldSettingsUpdated.EventName);
    }

    public static EventName: string = "MERCHANT_FORM_FIELD_SETTINGS_UPDATED";
}

export class MerchantErpIntegrationThinLoaded extends Event {
    constructor(public erpIntegrationThin: ErpIntegrationThin) {
        super(MerchantErpIntegrationThinLoaded.EventName);
    }

    public static EventName: string = "MERCHANT_ERP_INTEGRATION_THIN_LOADED";
}

export function updateMerchant(change: Partial<Merchant>, fallback: Partial<Merchant>): ActionWithDispatch<MerchantUpdated> {
    return function (dispatch) {
        const action = dispatch(new MerchantUpdated(change).toObject())
        return merchantService
            .updateMerchant(change)
            .then(() => action)
            .catch((error) => {
                dispatch(new MerchantUpdated(fallback).toObject());
                throw error;
            });
    };
}

export async function loadAllMerchants(dispatch: Dispatch<any>) {
    const merchants = await merchantService.listAllMerchants();
    dispatch(new MerchantsLoaded(merchants).toObject());
}

export function loadMerchantFees(dispatch: Dispatch): Promise<EventAction<MerchantFeesLoaded>> {
    return merchantService.listFees()
        .then(fees => dispatch(new MerchantFeesLoaded(fees).toObject()));
}

export function createMerchantFee(fee: Fee): ActionWithDispatch<MerchantFeesAdded> {
    return function (dispatch) {
        return merchantService
            .upsertFee(fee)
            .then(newFee => dispatch(new MerchantFeesAdded(newFee).toObject()));
    };
}

export function updateMerchantFee(newValue: Fee, oldValue: Fee): ActionWithDispatch<MerchantFeesUpdated> {
    return function (dispatch) {
        const action = dispatch(new MerchantFeesUpdated(newValue).toObject());
        return merchantService
            .upsertFee(newValue)
            .then(() => action)
            .catch((error) => {
                dispatch(new MerchantFeesUpdated(oldValue).toObject());
                throw error;
            });
    };
}

export function deleteMerchantFee(dispatch: Dispatch, fee: Fee): Promise<EventAction<MerchantFeesDeleted>> {
    return trackApiCallNew(dispatch, innerDispatch =>
        merchantService.deleteFees(fee.id).then(() => innerDispatch(new MerchantFeesDeleted(fee).toObject()))
    );
}

export function loadMerchantContractExtendedTypes(dispatch: Dispatch): Promise<EventAction<MerchantContractExtendedTypesLoaded>> {
    return merchantService.listContractExtendedTypes()
        .then(xt => dispatch(new MerchantContractExtendedTypesLoaded(xt).toObject()));
}

export function loadMerchantCustomFields(dispatch: Dispatch): Promise<EventAction<MerchantCustomFieldsLoaded>> {
    return merchantService.listCustomFields()
        .then(cf => dispatch(new MerchantCustomFieldsLoaded(cf).toObject()));
}

export function loadMerchantFormFieldSettings(dispatch: Dispatch): Promise<EventAction<MerchantFormFieldSettingsLoaded>> {
    return merchantService.listFormFieldSettings()
        .then(ffs => dispatch(new MerchantFormFieldSettingsLoaded(ffs).toObject()));
}

export function loadEditableContractFields(dispatch: Dispatch): Promise<EventAction<MerchantEditableContractFieldsLoaded>> {
    return merchantService.listEditableContractFields()
        .then(efs => dispatch(new MerchantEditableContractFieldsLoaded(efs).toObject()));
}

export function updateFormFieldSetting(newValue: FormFieldSetting, oldValue: FormFieldSetting): ActionWithDispatch<MerchantFormFieldSettingsUpdated> {
    return function (dispatch) {
        const action = dispatch(new MerchantFormFieldSettingsUpdated(newValue).toObject());
        return merchantService
            .upsertFormField(newValue)
            .then(() => action)
            .catch((error) => {
                dispatch(new MerchantFormFieldSettingsUpdated(oldValue).toObject());
                throw error;
            })
    };
}

export function loadMerchantTags(dispatch: Dispatch): Promise<EventAction<MerchantTagsLoaded>> {
    return merchantService.listTags()
        .then(t => dispatch(new MerchantTagsLoaded(t).toObject()));
}

export function loadMerchantPaymentOptions(dispatch: Dispatch): Promise<EventAction<MerchantPaymentOptionsLoaded>> {
    return merchantService.listPaymentOptions()
        .then(pod => dispatch(new MerchantPaymentOptionsLoaded(pod).toObject()));
}

export function loadMerchantErpIntegrationThin(dispatch: Dispatch): Promise<EventAction<MerchantErpIntegrationThinLoaded>> {
    return merchantService.getErpIntegration()
        .then(e => dispatch(new MerchantErpIntegrationThinLoaded(e).toObject()));
}